/**
 * useOnboardingNavigation Hook
 *
 * Manages onboarding wizard navigation state and logic
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { completeOnboarding } from "../services/onboardingService";
import { createLogger } from "../utils/logger";

/** Duration (ms) the celebration screen is displayed before auto-navigating */
export const CELEBRATION_DURATION_MS = 1500;

const onboardingLogger = createLogger({ prefix: "OnboardingNavigation" });

/**
 * Onboarding screen categories in order
 * All 6 screens in the onboarding flow
 */
export const ONBOARDING_CATEGORIES = [
  "profile",
  "investableAssets",
  "realEstateAssets",
  "liabilities",
  "income",
  "expenses",
  "assumptions",
] as const;

export type OnboardingCategory = (typeof ONBOARDING_CATEGORIES)[number];

interface UseOnboardingNavigationProps {
  currentCategory: OnboardingCategory;
  /** Optional validation callback invoked before navigating to the next screen.
   *  If it returns false, navigation is blocked. */
  validate?: () => Promise<boolean>;
}

interface UseOnboardingNavigationResult {
  /** Current screen index (0-based) */
  currentIndex: number;
  /** Total number of screens */
  totalScreens: number;
  /** Whether there is a previous screen */
  hasPrevious: boolean;
  /** Whether there is a next screen */
  hasNext: boolean;
  /** Whether this is the last screen */
  isLastScreen: boolean;
  /** Whether all screens have been viewed */
  allScreensViewed: boolean;
  /** Whether "Done" button should be shown */
  showDone: boolean;
  /** Navigate to previous screen */
  handlePrevious: () => void;
  /** Navigate to next screen */
  handleNext: () => void;
  /** Complete onboarding */
  handleDone: () => void;
  /** Whether onboarding is currently processing navigation/completion */
  isTransitioning: boolean;
  /** Whether the celebration screen should be displayed */
  showCelebration: boolean;
}

/**
 * Custom hook for onboarding wizard navigation
 *
 * Tracks:
 * - Current screen position
 * - Which screens have been viewed (using React state)
 * - When to show "Done" button
 *
 * @param props - Hook props
 * @returns Navigation state and handlers
 *
 * @example
 * ```tsx
 * function OnboardingProfile() {
 *   const nav = useOnboardingNavigation({ currentCategory: 'profile' });
 *
 *   return (
 *     <OnboardingModalLayout
 *       currentIndex={nav.currentIndex}
 *       totalScreens={nav.totalScreens}
 *       categoryName="Profile"
 *       hasPrevious={nav.hasPrevious}
 *       hasNext={nav.hasNext}
 *       showDone={nav.showDone}
 *       onPrevious={nav.handlePrevious}
 *       onNext={nav.handleNext}
 *       onDone={nav.handleDone}
 *     >
 *       <ProfileForm />
 *     </OnboardingModalLayout>
 *   );
 * }
 * ```
 */
export function useOnboardingNavigation({
  currentCategory,
  validate,
}: UseOnboardingNavigationProps): UseOnboardingNavigationResult {
  const navigate = useNavigate();
  const { user, updateOnboardingCompleted } = useAuth();
  const currentIndex = ONBOARDING_CATEGORIES.indexOf(currentCategory);
  const totalScreens = ONBOARDING_CATEGORIES.length;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Cleanup celebration timer on unmount
  useEffect(() => {
    return () => {
      if (celebrationTimerRef.current) {
        clearTimeout(celebrationTimerRef.current);
      }
    };
  }, []);

  // Track viewed screens with sessionStorage persistence so state survives page refresh
  const VIEWED_SCREENS_KEY = "onboarding_viewed_screens";

  const [viewedScreens, setViewedScreens] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(VIEWED_SCREENS_KEY);
      if (stored) {
        return new Set(JSON.parse(stored) as string[]);
      }
    } catch (error) {
      onboardingLogger.debug("sessionStorage unavailable for viewed screens", {
        error,
      });
    }
    return new Set();
  });

  // Mark current screen as viewed when component mounts or category changes
  useEffect(() => {
    setViewedScreens((prev) => {
      if (!prev.has(currentCategory)) {
        const newSet = new Set(prev);
        newSet.add(currentCategory);
        try {
          sessionStorage.setItem(
            VIEWED_SCREENS_KEY,
            JSON.stringify([...newSet])
          );
        } catch (error) {
          onboardingLogger.debug(
            "sessionStorage unavailable for viewed screens persist",
            { error }
          );
        }
        return newSet;
      }
      return prev;
    });
  }, [currentCategory]);

  // Determine navigation state
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalScreens - 1;
  const isLastScreen = currentIndex === totalScreens - 1;
  const allScreensViewed = viewedScreens.size === totalScreens;
  const showDone = isLastScreen && allScreensViewed;

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      const prevCategory = ONBOARDING_CATEGORIES[currentIndex - 1];
      navigate(`/onboarding/${prevCategory}`);
    }
  }, [currentIndex, hasPrevious, navigate]);

  const handleNext = useCallback(async () => {
    onboardingLogger.debug("handleNext called", {
      hasNext,
      isTransitioning,
      currentCategory,
      currentIndex,
    });

    if (!hasNext || isTransitioning) {
      onboardingLogger.debug("handleNext: early return", {
        reason: !hasNext ? "no next screen" : "already transitioning",
      });
      return;
    }
    setIsTransitioning(true);

    try {
      // If a validate callback is provided, invoke it before navigating
      if (validate) {
        const isValid = await validate();
        if (!isValid) {
          onboardingLogger.warn(
            "handleNext: blocking navigation due to validation failure",
            { currentCategory }
          );
          return;
        }
      }

      const nextCategory = ONBOARDING_CATEGORIES[currentIndex + 1];
      onboardingLogger.info("handleNext: navigating to next screen", {
        from: currentCategory,
        to: nextCategory,
        path: `/onboarding/${nextCategory}`,
      });
      navigate(`/onboarding/${nextCategory}`);
    } finally {
      setIsTransitioning(false);
    }
  }, [
    currentIndex,
    hasNext,
    isTransitioning,
    navigate,
    currentCategory,
    validate,
  ]);

  const handleDone = useCallback(async () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // Show celebration immediately for instant user feedback
    setShowCelebration(true);

    try {
      // Persist to Firestore
      if (!user?.userId) {
        // CRITICAL: No user ID available - this should never happen but handle gracefully
        onboardingLogger.error("handleDone: No user ID available!", { user });
      } else {
        const result = await completeOnboarding(user.userId);

        if (!result.ok) {
          onboardingLogger.error(
            "Postcondition failed: completeOnboarding returned error",
            {
              userId: user.userId,
              error: result.error,
              currentCategory,
              showDone,
              allScreensViewed,
            }
          );
          // Don't show alert, just log the error for debugging
          // Still show celebration - user can complete onboarding later if needed
        } else {
          // Update AuthContext state to indicate onboarding is complete
          // This provides immediate feedback without waiting for Firestore listener
          updateOnboardingCompleted(true);
        }
      }

      // Clean up sessionStorage tracking since onboarding is complete
      try {
        sessionStorage.removeItem(VIEWED_SCREENS_KEY);
      } catch (error) {
        onboardingLogger.debug("sessionStorage cleanup failed", { error });
      }

      // Auto-navigate to dashboard after celebration delay
      celebrationTimerRef.current = setTimeout(() => {
        navigate("/dashboard");
      }, CELEBRATION_DURATION_MS);
    } catch (error) {
      onboardingLogger.error("Error during onboarding completion", { error });
      // On unexpected error, navigate directly (skip celebration)
      navigate("/dashboard");
    } finally {
      setIsTransitioning(false);
    }
  }, [
    allScreensViewed,
    currentCategory,
    isTransitioning,
    navigate,
    showDone,
    user,
    updateOnboardingCompleted,
  ]);

  return {
    currentIndex,
    totalScreens,
    hasPrevious,
    hasNext,
    isLastScreen,
    allScreensViewed,
    showDone,
    handlePrevious,
    handleNext,
    handleDone,
    isTransitioning,
    showCelebration,
  };
}
